import { v1ApiClient } from '@/services';
import { getCurrWorkspaceAlias } from '@/utils/workspace';

import type { ProductT } from '../types/types';
import type {
  CreateProductPayloadDTO,
  CreateProductResponseDTO,
  FetchProductListResponseDTO,
  MergeBrandsPayload,
  MergeLinesPayload,
  MergeProductOptionsPayload,
  MergeProductsPayload,
  UpdateBrandPayload,
  UpdateLinePayload,
  UpdateProductOptionPayload,
  UpdateProductPayload,
} from './dto';

export const productInfoService = {
  // Products
  fetchProductList: async (
    workspaceAlias: string,
    isPublic?: boolean,
  ): Promise<FetchProductListResponseDTO> => {
    const url = isPublic
      ? `/workspaces/${workspaceAlias}/products/public`
      : `/workspaces/${workspaceAlias}/products`;
    const response = await v1ApiClient.get(url);
    return response.data;
  },

  createProducts: async (
    payload: CreateProductPayloadDTO,
  ): Promise<CreateProductResponseDTO> => {
    const wa = getCurrWorkspaceAlias();
    const response = await v1ApiClient.post(
      `/workspaces/${wa}/products`,
      payload,
    );
    return response.data;
  },

  updateProduct: async (
    productId: string,
    payload: UpdateProductPayload,
  ): Promise<ProductT> => {
    const wa = getCurrWorkspaceAlias();
    const response = await v1ApiClient.put(
      `/workspaces/${wa}/products/${productId}`,
      payload,
    );
    return response.data;
  },

  deleteProduct: async (productId: string): Promise<void> => {
    const wa = getCurrWorkspaceAlias();
    await v1ApiClient.delete(`/workspaces/${wa}/products/${productId}`);
  },

  mergeProducts: async (payload: MergeProductsPayload): Promise<void> => {
    const wa = getCurrWorkspaceAlias();
    await v1ApiClient.post(`/workspaces/${wa}/products/merge`, payload);
  },

  // Brands
  updateBrand: async (
    brandId: string,
    payload: UpdateBrandPayload,
  ): Promise<ProductBrandOption> => {
    const wa = getCurrWorkspaceAlias();
    const response = await v1ApiClient.put(
      `/workspaces/${wa}/brands/${brandId}`,
      payload,
    );
    return response.data;
  },

  deleteBrand: async (brandId: string): Promise<void> => {
    const wa = getCurrWorkspaceAlias();
    await v1ApiClient.delete(`/workspaces/${wa}/brands/${brandId}`);
  },

  mergeBrands: async (payload: MergeBrandsPayload): Promise<void> => {
    const wa = getCurrWorkspaceAlias();
    await v1ApiClient.post(`/workspaces/${wa}/brands/merge`, payload);
  },

  // Lines
  updateLine: async (
    lineId: string,
    payload: UpdateLinePayload,
  ): Promise<ProductLineOption> => {
    const wa = getCurrWorkspaceAlias();
    const response = await v1ApiClient.put(
      `/workspaces/${wa}/lines/${lineId}`,
      payload,
    );
    return response.data;
  },

  deleteLine: async (lineId: string): Promise<void> => {
    const wa = getCurrWorkspaceAlias();
    await v1ApiClient.delete(`/workspaces/${wa}/lines/${lineId}`);
  },

  mergeLines: async (payload: MergeLinesPayload): Promise<void> => {
    const wa = getCurrWorkspaceAlias();
    await v1ApiClient.post(`/workspaces/${wa}/lines/merge`, payload);
  },

  // Product Options
  updateProductOption: async (
    productOptionId: string,
    payload: UpdateProductOptionPayload,
  ): Promise<ProductT> => {
    const wa = getCurrWorkspaceAlias();
    const response = await v1ApiClient.put(
      `/workspaces/${wa}/product_options/${productOptionId}`,
      payload,
    );
    return response.data;
  },

  deleteProductOption: async (productOptionId: string): Promise<void> => {
    const wa = getCurrWorkspaceAlias();
    await v1ApiClient.delete(
      `/workspaces/${wa}/product_options/${productOptionId}`,
    );
  },

  mergeProductOptions: async (
    payload: MergeProductOptionsPayload,
  ): Promise<void> => {
    const wa = getCurrWorkspaceAlias();
    await v1ApiClient.post(`/workspaces/${wa}/product_options/merge`, payload);
  },
};
